/**
 * BookmarksTab Component
 * Study bookmarks management - list and add form
 */

import React, { useState } from 'react';
import { ExternalLink, Eye, EyeOff, Edit2, Trash2, Link, Plus, Globe, RefreshCw, X } from 'lucide-react';
import type { StudyBookmark, BookmarkAssignment, AddBookmarkFormData, BookmarkType, SiteUser } from '../types';
import { BOOKMARK_TYPES } from '../types';
import { fieldCls } from '../../ui';

interface BookmarksTabProps {
  bookmarks: StudyBookmark[];
  loading: boolean;
  onAddBookmark: (data: AddBookmarkFormData) => Promise<unknown>;
  onUpdateBookmark: (bookmarkId: string, data: Partial<StudyBookmark>) => Promise<unknown>;
  onRemoveBookmark: (bookmarkId: string) => Promise<unknown>;
  // Per-link portal-access assignments (who has been granted access to the
  // portal this link points at). Visibility of links is unchanged.
  assignments: BookmarkAssignment[];
  siteUsers: SiteUser[];
  onAssignUser: (bookmarkId: string, userId: string) => Promise<unknown>;
  onUnassignUser: (bookmarkId: string, userId: string) => Promise<unknown>;
}

export function BookmarksTab({
  bookmarks,
  loading,
  onAddBookmark,
  onUpdateBookmark,
  onRemoveBookmark,
  assignments,
  siteUsers,
  onAssignUser,
  onUnassignUser,
}: BookmarksTabProps) {
  const [formData, setFormData] = useState<AddBookmarkFormData>({
    bookmark_type: 'Other' as BookmarkType,
    label: '',
    url: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<StudyBookmark>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.bookmark_type || !formData.label || !formData.url) return;

    setSaving(true);
    setError(null);
    try {
      await onAddBookmark(formData);
      setFormData({ bookmark_type: 'Other' as BookmarkType, label: '', url: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add bookmark');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (bookmark: StudyBookmark) => {
    setEditingId(bookmark.id);
    setEditData({
      bookmark_type: bookmark.bookmark_type,
      label: bookmark.label,
      url: bookmark.url,
    });
  };

  const handleSaveEdit = async (bookmarkId: string) => {
    try {
      await onUpdateBookmark(bookmarkId, editData);
      setEditingId(null);
      setEditData({});
    } catch (err: any) {
      console.error('Error updating bookmark:', err);
    }
  };

  const getDomain = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  const assignmentsFor = (bookmarkId: string) =>
    assignments.filter(a => a.bookmark_id === bookmarkId);

  const handleAssign = async (bookmarkId: string, userId: string) => {
    if (!userId) return;
    try {
      await onAssignUser(bookmarkId, userId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to assign user');
    }
  };

  const handleUnassign = async (bookmarkId: string, userId: string) => {
    try {
      await onUnassignUser(bookmarkId, userId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove access');
    }
  };

  const firstName = (fullName: string) => (fullName || '').trim().split(/\s+/)[0] || fullName;

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left: Bookmarks list */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Bookmarks</h3>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-100 dark:bg-gray-800 rounded-lg h-12 animate-pulse" />
            ))}
          </div>
        ) : bookmarks.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Link size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No bookmarks yet</p>
            <p className="text-sm">Add study portals and links</p>
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 w-12"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Label</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">URL</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Access</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {bookmarks.map((bookmark) => (
                  <tr key={bookmark.id}>
                    {/* Favicon column */}
                    <td className="px-4 py-3">
                      <div className="w-6 h-6 flex items-center justify-center">
                        {bookmark.favicon_url ? (
                          <img
                            src={bookmark.favicon_url}
                            alt=""
                            className="w-5 h-5 rounded"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <Globe
                          size={18}
                          className={`text-gray-400 ${bookmark.favicon_url ? 'hidden' : ''}`}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {editingId === bookmark.id ? (
                        <select
                          value={editData.bookmark_type}
                          onChange={e => setEditData(prev => ({ ...prev, bookmark_type: e.target.value as BookmarkType }))}
                          className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        >
                          {BOOKMARK_TYPES.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-sm text-gray-700 dark:text-gray-200">{bookmark.bookmark_type}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === bookmark.id ? (
                        <input
                          type="text"
                          value={editData.label}
                          onChange={e => setEditData(prev => ({ ...prev, label: e.target.value }))}
                          className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 w-full"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{bookmark.label}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === bookmark.id ? (
                        <input
                          type="url"
                          value={editData.url}
                          onChange={e => setEditData(prev => ({ ...prev, url: e.target.value }))}
                          className="px-2 py-1 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 w-full"
                        />
                      ) : (
                        <a
                          href={bookmark.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                          <span className="truncate max-w-xs">{getDomain(bookmark.url)}</span>
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </td>
                    {/* Access: who has been granted access to this portal.
                        Metadata only — the link stays visible to everyone. */}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        {assignmentsFor(bookmark.id).map((a) => (
                          <span
                            key={a.user_id}
                            className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                            title={a.user?.email || a.user?.full_name || ''}
                          >
                            {firstName(a.user?.full_name || '?')}
                            <button
                              onClick={() => handleUnassign(bookmark.id, a.user_id)}
                              className="rounded-full p-0.5 text-primary-600 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/40"
                              title="Remove access"
                              aria-label={`Remove ${a.user?.full_name || 'user'} from ${bookmark.label}`}
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                        <select
                          value=""
                          onChange={e => handleAssign(bookmark.id, e.target.value)}
                          className="rounded-lg border border-dashed border-gray-300 bg-transparent px-1.5 py-0.5 text-xs text-gray-500 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-400"
                          title="Grant a user access to this portal"
                          aria-label={`Assign user to ${bookmark.label}`}
                        >
                          <option value="">+ Assign</option>
                          {siteUsers
                            .filter(u => !assignmentsFor(bookmark.id).some(a => a.user_id === u.id))
                            .map(u => (
                              <option key={u.id} value={u.id}>
                                {u.full_name || u.email}
                              </option>
                            ))}
                        </select>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {editingId === bookmark.id ? (
                          <>
                            <button
                              onClick={() => handleSaveEdit(bookmark.id)}
                              className="px-2 py-1 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-150"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingId(null);
                                setEditData({});
                              }}
                              className="px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg transition-colors duration-150"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => onUpdateBookmark(bookmark.id, { refresh_favicon: true } as any)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                              title="Refresh favicon"
                            >
                              <RefreshCw size={14} className="text-gray-400 dark:text-gray-500" />
                            </button>
                            <button
                              onClick={() => onUpdateBookmark(bookmark.id, {
                                is_hidden_default: !bookmark.is_hidden_default,
                              })}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                              title={bookmark.is_hidden_default ? 'Show by default' : 'Hide by default'}
                            >
                              {bookmark.is_hidden_default ? (
                                <EyeOff size={14} className="text-gray-400 dark:text-gray-500" />
                              ) : (
                                <Eye size={14} className="text-gray-400 dark:text-gray-500" />
                              )}
                            </button>
                            <button
                              onClick={() => handleEdit(bookmark)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                              title="Edit"
                            >
                              <Edit2 size={14} className="text-gray-400 dark:text-gray-500" />
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Delete this bookmark?')) {
                                  onRemoveBookmark(bookmark.id);
                                }
                              }}
                              className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150"
                              title="Delete"
                            >
                              <Trash2 size={14} className="text-red-400" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Right: Add form */}
      <div className="w-full lg:w-80 lg:flex-shrink-0">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Add Bookmark</h4>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type
              </label>
              <select
                value={formData.bookmark_type}
                onChange={e => setFormData(prev => ({ ...prev, bookmark_type: e.target.value as BookmarkType }))}
                className={`w-full ${fieldCls}`}
                required
              >
                {BOOKMARK_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                URL
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={e => setFormData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://..."
                className={`w-full ${fieldCls}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Label
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={e => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="i.e. Fedex, CRIO, etc."
                className={`w-full ${fieldCls}`}
                required
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            )}

            <button
              type="submit"
              disabled={saving || !formData.bookmark_type || !formData.label || !formData.url}
              className="w-full inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              <Plus size={16} />
              {saving ? 'Adding...' : 'Add Bookmark'}
            </button>
          </form>

          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Pro tip: Use our browser extension to quickly add bookmarks from any page.
          </p>
        </div>
      </div>
    </div>
  );
}

export default BookmarksTab;
